import React, { Dispatch, SetStateAction } from 'react';
import { IconButton, Stack, Tooltip } from '@mui/material';
import moment, { Moment } from 'moment';
import { useForm } from 'react-hook-form';
import { DateField } from 'components/form';
import { Close, Save } from '@mui/icons-material';
import { useVO } from 'modules/vendorOpportunity';

import { ReportsListingViewModel } from '../../../types';
import { useDealLifeCycle } from '../../../api/dealLifeCycle';

interface FormValues {
  date: Moment | '';
}

interface Props {
  row: ReportsListingViewModel;
  submitMode: 'closeDate' | 'estimatedCloseDate';
  setViewMode: Dispatch<
    SetStateAction<{
      viewType: 'view' | 'edit';
      submitMode: 'closeDate' | 'estimatedCloseDate';
    }>
  >;
}

export const Edit = ({ row, setViewMode, submitMode }: Props) => {
  const { updateEstimatedCloseDate, isVOEstimatedCloseDateUpdating } = useVO();
  const { actions, loading } = useDealLifeCycle();

  const { control, getValues, reset } = useForm<FormValues>({
    defaultValues: {
      date: row[submitMode] ? moment(row[submitMode]) : '',
    },
  });

  const handleSave = async () => {
    const value = getValues('date');

    if (typeof value !== 'object') return;

    if (submitMode === 'estimatedCloseDate') {
      await updateEstimatedCloseDate({ voId: row.id, estimatedCloseDate: value.toISOString() }, (_, bpfApi) => ({
        refetchQueries: [bpfApi.report.getReport],
        awaitRefetchQueries: true,
      }));
    }

    if (submitMode === 'closeDate') {
      await actions.handleUpdateDealClosureDate({ id: row.id, closedDateTime: value.toISOString() }, (_, bpfApi) => ({
        refetchQueries: [bpfApi.report.getReport],
        awaitRefetchQueries: true,
      }));
    }

    setViewMode({ viewType: 'view', submitMode: 'estimatedCloseDate' });
    reset();
  };

  const handleCancel = () => {
    setViewMode({ viewType: 'view', submitMode: 'estimatedCloseDate' });
    reset();
  };

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" gap="1rem">
      <DateField
        name="date"
        label="Date"
        control={control}
        slotProps={{
          textField: { size: 'small', variant: 'outlined' },
        }}
      />

      <Stack direction="row" gap="0.5rem">
        <Tooltip arrow placement="top" title="Save">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleSave}
              disabled={isVOEstimatedCloseDateUpdating || loading.isUpdatingDealClosureDate}
            >
              <Save fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip arrow placement="top" title="Cancel">
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={handleCancel}
              disabled={isVOEstimatedCloseDateUpdating || loading.isUpdatingDealClosureDate}
            >
              <Close fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
};
